import React, { useState, useEffect, useRef } from "react";
import BitrixAPI from "../api/b24";
import FilePreview from "../components/FileViewer";
import LoadingButton from "../components/LoadingButton";
import PrintButton from "../components/PrintButton";
import FileUpload from "../components/FileUpload";
import { API_URL } from '../config';
import { useHeader } from "../contexts/HeaderContext";

const MAX_MEDIA_FILE_SIZE = 150 * 1024 * 1024;

const SendOrdersPageB24 = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingOrders, setProcessingOrders] = useState(new Set());
  const [error, setError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [groupedOrders, setGroupedOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const { setHeaderExtras } = useHeader();

  // Состояние для polling новых заказов
  const [isPollingActive, setIsPollingActive] = useState(true);
  const [newOrdersNotification, setNewOrdersNotification] = useState(null); // {count: number, orderNumbers: string[]}
  
  // Компонент свайп-кнопки (скопированный из CuttingOrdersPageB24)
  const SwipeToConfirmButton = ({ orderId, onConfirm, isLoading }) => {
    const [dragX, setDragX] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [startX, setStartX] = useState(0);
    const maxDrag = 200; // Максимальное расстояние для свайпа
    const threshold = 150; // Пороговое значение для активации
    const sliderRef = React.useRef(null);
    
    const handleSliderMouseDown = (e) => {
      setIsDragging(true);
      setStartX(e.clientX);
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleSliderTouchStart = (e) => {
      setIsDragging(true);
      setStartX(e.touches[0].clientX);
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleGlobalMouseMove = (e) => {
      if (!isDragging) return;
      const deltaX = e.clientX - startX;
      setDragX(Math.max(0, Math.min(deltaX, maxDrag)));
      e.preventDefault();
    };
    
    const handleGlobalTouchMove = (e) => {
      if (!isDragging) return;
      const deltaX = e.touches[0].clientX - startX;
      setDragX(Math.max(0, Math.min(deltaX, maxDrag)));
      e.preventDefault();
    };
    
    const handleEnd = () => {
      if (dragX >= threshold && !isCompleted) {
        setIsCompleted(true);
        onConfirm(orderId);
      }
      setIsDragging(false);
      setDragX(0);
      setStartX(0);
    };
    
    // Добавляем глобальные обработчики только при перетаскивании
    React.useEffect(() => {
      if (isDragging) {
        document.addEventListener('mousemove', handleGlobalMouseMove, { passive: false });
        document.addEventListener('mouseup', handleEnd);
        document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
        document.addEventListener('touchend', handleEnd);
        
        return () => {
          document.removeEventListener('mousemove', handleGlobalMouseMove);
          document.removeEventListener('mouseup', handleEnd);
          document.removeEventListener('touchmove', handleGlobalTouchMove);
          document.removeEventListener('touchend', handleEnd);
        };
      }
    }, [isDragging, startX, dragX, threshold, isCompleted]);
    
    const progress = (dragX / maxDrag) * 100;
    const isActivated = dragX >= threshold;
    
    return (
      <div className="relative w-full h-16 sm:h-12 bg-gray-200 rounded-lg overflow-hidden select-none pointer-events-none">
        {/* Прогресс-бар */}
        <div 
          className={`absolute inset-y-0 left-0 transition-colors duration-200 pointer-events-none ${
            isActivated ? 'bg-green-400' : 'bg-blue-400'
          }`}
          style={{ width: `${progress}%` }}
        />
        
        {/* Слайдер - только здесь можно начать перетаскивание */}
        <div 
          ref={sliderRef}
          className={`absolute top-1 left-1 w-14 h-14 sm:w-10 sm:h-10 rounded-md shadow-md transition-colors duration-200 flex items-center justify-center cursor-grab pointer-events-auto ${
            isActivated ? 'bg-green-600' : 'bg-blue-600'
          } ${isDragging ? 'cursor-grabbing' : ''}`}
          style={{ 
            transform: `translateX(${dragX}px)`,
            transition: isDragging ? 'none' : 'transform 0.3s ease'
          }}
          onMouseDown={handleSliderMouseDown}
          onTouchStart={handleSliderTouchStart}
        >
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <span className="text-white text-xs">→</span>
          )}
        </div>
        
        {/* Текст */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className={`text-sm font-medium transition-colors duration-200 ${
            isActivated ? 'text-white' : 'text-gray-600'
          }`}>
            {isLoading ? 'Обработка...' : isActivated ? 'Отпустите для завершения' : 'Свайп для завершения →'}
          </span>
        </div>
      </div>
    );
  };

  const handleStageUpdate = async (orderId) => {
    try {
      setProcessingOrders(prev => new Set([...prev, orderId]));
      setError(null);
      
      const result = await BitrixAPI.updateStageSend(orderId);
      
      if (result && result.success) {
        setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
        console.log(`Заказ ${orderId} успешно переведен из упаковки`);
      } else {
        throw new Error(result?.message || "Не удалось обновить стадию");
      }
    } catch (error) {
      console.error("Ошибка при обновлении стадии:", error);
      setError(`Ошибка при обновлении заказа ${orderId}: ${error.message}`);
      alert(`Ошибка: ${error.message}`);
    } finally {
      setProcessingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const handleUploadSuccess = (message) => {
    setUploadSuccess(message);
    setUploadError(null);
    // Автоматически скрыть уведомление через 5 секунд
    setTimeout(() => setUploadSuccess(null), 5000);
  };

  const handleUploadError = (message) => {
    setUploadError(message);
    setUploadSuccess(null);
  };

  // Функция группировки заказов по первым 3 буквам
  const groupOrdersByPrefix = (ordersArray) => {
    const groups = {};
    
    ordersArray.forEach(order => {
      if (order.orderNumber) {
        const prefix = order.orderNumber.substring(0, 3).toUpperCase();
        if (!groups[prefix]) {
          groups[prefix] = [];
        }
        groups[prefix].push(order);
      }
    });
    
    return Object.entries(groups).map(([prefix, orders]) => ({
      prefix,
      orders,
      selectedOrders: []
    }));
  };

  // Функция переключения выбора заказа в группе
  const toggleOrderSelection = (groupIndex, orderId) => {
    setGroupedOrders(prev => {
      const newGroups = prev.map((group, index) => {
        if (index === groupIndex) {
          // Создаем новый объект группы вместо мутации существующего
          const newSelectedOrders = group.selectedOrders.includes(orderId)
            ? group.selectedOrders.filter(id => id !== orderId)
            : [...group.selectedOrders, orderId];
          
          return {
            ...group,
            selectedOrders: newSelectedOrders
          };
        }
        return group;
      });
      
      return newGroups;
    });
  };

  // Функция загрузки файла для группы
  const handleGroupFileUpload = async (groupIndex, files) => {
    const group = groupedOrders[groupIndex];
    const selectedOrderIds = group.selectedOrders;
    
    if (selectedOrderIds.length === 0) {
      setUploadError('Выберите заказы в группе для загрузки файла');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      let successCount = 0;
      const failureReasons = [];
      
      // Загружаем файлы для каждого выбранного заказа
      for (const orderId of selectedOrderIds) {
        for (const file of files) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('orderId', orderId);
          formData.append('fieldType', 'packaging');

          try {
            const response = await fetch(`${API_URL}/api/upload-packaging-file`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`
              },
              body: formData
            });

            const result = await response.json().catch(() => ({}));
            if (result.success) {
              successCount++;
            } else {
              const reason = response.status === 413
                ? 'файл превышает допустимый размер 150 МБ'
                : (result.message || `сервер вернул ошибку ${response.status}`);
              failureReasons.push(`Заказ ${orderId}, ${file.name}: ${reason}`);
              console.error(`Ошибка загрузки для заказа ${orderId}:`, reason);
            }
          } catch (fileError) {
            failureReasons.push(`Заказ ${orderId}, ${file.name}: ${fileError.message}`);
            console.error(`Ошибка загрузки файла ${file.name} для заказа ${orderId}:`, fileError);
          }
        }
      }
      
      if (successCount > 0) {
        setUploadSuccess(`Файлы успешно загружены: ${successCount} загрузок`);
        setUploadError(null);
      } else {
        setUploadError(`Не удалось загрузить ни одного файла. ${failureReasons[0] || 'Повторите попытку.'}`);
      }
    } catch (error) {
      setUploadError(`Ошибка загрузки файлов: ${error.message}`);
      setUploadSuccess(null);
    }
  };

  // Функция обработки группы заказов (общий свайп)
  const handleGroupStageUpdate = async (groupIndex) => {
    const group = groupedOrders[groupIndex];
    const selectedOrderIds = group.selectedOrders;
    
    if (selectedOrderIds.length === 0) {
      setError('Выберите заказы в группе для завершения');
      return;
    }

    try {
      // Добавляем все выбранные заказы в список обрабатываемых
      setProcessingOrders(prev => new Set([...prev, ...selectedOrderIds]));
      setError(null);
      
      let successCount = 0;
      let errorCount = 0;
      
      // Обрабатываем каждый выбранный заказ последовательно
      for (const orderId of selectedOrderIds) {
        try {
          const result = await BitrixAPI.updateStageSend(orderId);
          
          if (result && result.success) {
            successCount++;
            console.log(`Заказ ${orderId} успешно переведен из упаковки`);
          } else {
            throw new Error(result?.message || "Не удалось обновить стадию");
          }
        } catch (orderError) {
          console.error(`Ошибка при обновлении заказа ${orderId}:`, orderError);
          errorCount++;
        }
      }
      
      // Обновляем список заказов, убирая успешно обработанные
      if (successCount > 0) {
        setOrders(prevOrders => 
          prevOrders.filter(order => !selectedOrderIds.includes(order.id))
        );
        
        // Обновляем группированные заказы
        const response = await BitrixAPI.getActiveOrdersSend();
        if (response && response.length > 0) {
          const grouped = groupOrdersByPrefix(response);
          setGroupedOrders(grouped);
        } else {
          setGroupedOrders([]);
        }
      }
      
      // Показываем результат
      if (successCount > 0 && errorCount === 0) {
        setUploadSuccess(`Все заказы (${successCount}) успешно завершены!`);
      } else if (successCount > 0 && errorCount > 0) {
        setError(`Завершено ${successCount} заказов, ошибки в ${errorCount} заказах`);
      } else {
        setError(`Не удалось завершить ни одного заказа из ${selectedOrderIds.length}`);
      }
      
    } catch (error) {
      console.error('Ошибка при групповом обновлении:', error);
      setError(`Ошибка при групповом обновлении: ${error.message}`);
    } finally {
      // Убираем все заказы из списка обрабатываемых
      setProcessingOrders(prev => {
        const newSet = new Set(prev);
        selectedOrderIds.forEach(orderId => newSet.delete(orderId));
        return newSet;
      });
    }
  };

  // Функция для загрузки заказов (выделена для повторного использования)
  const fetchOrders = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      const response = await BitrixAPI.getActiveOrdersSend();

      if (response && Array.isArray(response)) {
        // Определяем новые заказы (которых еще нет в текущем списке)
        const newOrders = response.filter(newOrder =>
          !orders.some(existingOrder => existingOrder.id === newOrder.id)
        );

        if (newOrders.length > 0 && !isInitialLoad) {
          console.log(`✨ Обнаружено ${newOrders.length} новых заказов отправки:`, newOrders.map(o => o.id));

          // Показываем уведомление о новых заказах
          setNewOrdersNotification({
            count: newOrders.length,
            orderNumbers: newOrders.map(o => o.orderNumber || o.id)
          });

          // Воспроизводим звук уведомления (если доступен)
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURE=');
            audio.play().catch(err => console.log('Не удалось воспроизвести звук:', err));
          } catch (err) {
            console.log('Ошибка создания аудио:', err);
          }

          // Автоматически скрываем уведомление через 10 секунд
          setTimeout(() => {
            setNewOrdersNotification(null);
          }, 10000);
        }

        setOrders(response || []);

        // Группируем заказы после загрузки
        if (response.length > 0) {
          const grouped = groupOrdersByPrefix(response);
          setGroupedOrders(grouped);
        }
      }
    } catch (err) {
      console.error("Ошибка загрузки заказов:", err);
      setError(`Ошибка загрузки заказов: ${err.message}`);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  // Вставка поиска и счётчика в хедер
  useEffect(() => {
    setHeaderExtras(
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <input
          type="text"
          placeholder="Поиск по номеру, названию..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          style={{ width: "220px" }}
        />
        <span className="text-sm text-gray-600">
          Заказов: <strong>{orders.length}</strong>
        </span>
      </div>
    );
    return () => setHeaderExtras(null);
  }, [searchQuery, orders.length]);

  // Первоначальная загрузка заказов
  useEffect(() => {
    fetchOrders(true);
  }, []);

  // Polling для автоматического обновления заказов
  useEffect(() => {
    if (!isPollingActive) {
      return;
    }

    const pollingInterval = setInterval(() => {
      console.log('🔄 Проверка новых заказов отправки...');
      fetchOrders(false);
    }, 30000); // Проверяем каждые 30 секунд

    return () => {
      clearInterval(pollingInterval);
    };
  }, [isPollingActive, orders]);

  if (loading) return <div className="p-4">Ойланып жатырмын ...</div>;

  return (
    <div className="container mx-auto p-4">
      


      {/* Уведомление о новых заказах */}
      {newOrdersNotification && (
        <div className="bg-green-50 border-2 border-green-400 text-green-800 px-4 py-3 rounded-lg mb-4 shadow-lg animate-pulse">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <strong className="text-lg">Поступило {newOrdersNotification.count} новых заказов отправки!</strong>
                <div className="text-sm text-green-700 mt-1">
                  Заказы: {newOrdersNotification.orderNumbers.join(', ')}
                </div>
              </div>
            </div>
            <button
              onClick={() => setNewOrdersNotification(null)}
              className="text-green-600 hover:text-green-800 transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Отображение ошибок */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <strong>Ошибка:</strong> {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ✕
          </button>
        </div>
      )}

      {/* Уведомления о загрузке файлов */}
      {uploadSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
          <strong>Успех:</strong> {uploadSuccess}
          <button 
            onClick={() => setUploadSuccess(null)}
            className="ml-2 text-green-500 hover:text-green-700"
          >
            ✕
          </button>
        </div>
      )}

      {uploadError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <strong>Ошибка загрузки:</strong> {uploadError}
          <button 
            onClick={() => setUploadError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ✕
          </button>
        </div>
      )}
      
      <div className="space-y-6">
        {groupedOrders && groupedOrders.length > 0 ? (
          groupedOrders.filter((group) => {
            if (!searchQuery.trim()) return true;
            const q = searchQuery.toLowerCase();
            return (
              group.prefix.toLowerCase().includes(q) ||
              group.orders.some(order =>
                String(order.orderNumber || "").toLowerCase().includes(q) ||
                String(order.title || "").toLowerCase().includes(q) ||
                String(order.id || "").toLowerCase().includes(q)
              )
            );
          }).map((group, groupIndex) => (
            <div key={group.prefix} className="bg-white border border-gray-200 rounded-lg shadow-sm">
              {/* Заголовок группы */}
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 rounded-t-lg">
                <h2 className="text-lg font-semibold text-gray-800">
                  Группа: {group.prefix}*** ({group.orders.length} заказов)
                </h2>
              </div>
              
              {/* Заказы в группе */}
              <div className="p-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-4">
                  {group.orders.map((order) => (
                    <div
                      key={order.id}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleOrderSelection(groupIndex, order.id);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                      className={`p-3 text-sm border-2 rounded-lg cursor-pointer transition-all duration-300 select-none active:scale-95 ${
                        group.selectedOrders.includes(order.id)
                          ? 'bg-blue-100 border-blue-500 text-blue-700 shadow-lg transform scale-105'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-blue-300 hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-base">{order.orderNumber}</div>
                          <div className="text-xs truncate text-gray-500">{order.title}</div>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ml-2 flex-shrink-0 ${
                          group.selectedOrders.includes(order.id)
                            ? 'bg-blue-500 border-blue-500'
                            : 'border-gray-400 bg-white'
                        }`}>
                          {group.selectedOrders.includes(order.id) && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Счетчик выбранных заказов и кнопки управления */}
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Выбрано заказов: <span className="font-medium text-blue-600">{group.selectedOrders.length}</span> из {group.orders.length}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        // Выбрать все заказы в группе
                        setGroupedOrders(prev => prev.map((g, index) => 
                          index === groupIndex 
                            ? { ...g, selectedOrders: g.orders.map(order => order.id) }
                            : g
                        ));
                      }}
                      className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                      disabled={group.selectedOrders.length === group.orders.length}
                    >
                      Выбрать все
                    </button>
                    <button
                      onClick={() => {
                        // Снять выбор со всех заказов в группе
                        setGroupedOrders(prev => prev.map((g, index) => 
                          index === groupIndex 
                            ? { ...g, selectedOrders: [] }
                            : g
                        ));
                      }}
                      className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                      disabled={group.selectedOrders.length === 0}
                    >
                      Снять выбор
                    </button>
                  </div>
                </div>
                
                {/* Загрузка файла для группы */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Загрузить файл для выбранных заказов:
                  </label>
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      const oversizedFile = files.find(file => file.size > MAX_MEDIA_FILE_SIZE);
                      if (oversizedFile) {
                        setUploadError(`Файл «${oversizedFile.name}» больше 150 МБ. Уменьшите видео и повторите загрузку.`);
                      } else if (files.length > 0) {
                        handleGroupFileUpload(groupIndex, files);
                        // Сбрасываем значение input для возможности повторной загрузки
                      }
                      e.target.value = '';
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    disabled={group.selectedOrders.length === 0}
                  />
                  {group.selectedOrders.length === 0 && (
                    <p className="text-xs text-red-500 mt-1">
                      Сначала выберите заказы для загрузки файлов
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Фото и видео до 150 МБ на один файл</p>
                </div>
                
                {/* Общий свайп для всех выбранных заказов в группе */}
                {group.selectedOrders.length > 0 && (
                  <div className="mt-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      Завершить выбранные заказы ({group.selectedOrders.length}):
                    </div>
                    <SwipeToConfirmButton
                      orderId={`group_${groupIndex}`}
                      onConfirm={() => handleGroupStageUpdate(groupIndex)}
                      isLoading={group.selectedOrders.some(id => processingOrders.has(id))}
                    />
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="bg-gray-50 p-8 rounded-lg text-center">
            <p className="text-gray-600">Нет заказов для отображения</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SendOrdersPageB24;
